import type { WalletTopupMode, WalletUser } from '@/api/resources/company-wallet'
import type { GrantSchedule } from '@/components/company-wallet/company-wallet-schemas'
import { useDebouncedValue } from '@tanstack/react-pacer'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { ApiError } from '@/api/client'
import {
  addWalletUsers,
  clearWalletSetting,
  deleteWalletUser,
  grantWalletCredit,
  updateWalletSetting,
  walletUsersQueryOptions,
} from '@/api/resources/company-wallet'
import { organisationQueryOptions } from '@/api/resources/organisations'
import { AddWalletUsersDialog } from '@/components/company-wallet/add-wallet-users-dialog'
import { CompanyWalletIntro } from '@/components/company-wallet/company-wallet-intro'
import { GrantCreditDialog } from '@/components/company-wallet/grant-credit-dialog'
import { WalletMembersTable } from '@/components/company-wallet/wallet-members-table'
import { WalletSelectionBar } from '@/components/company-wallet/wallet-selection-bar'
import { OrganisationNavSelect } from '@/components/layout/organisation-nav-select'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { useServerErrors } from '@/hooks/use-server-errors'
import { formatCurrencyFromDecimal } from '@/lib/format'
import { queryKeys } from '@/lib/query-keys'

function apiMessage(error: Error, fallback: string): string {
  return error instanceof ApiError ? error.body.message : fallback
}

// Array rules answer with keys like emails.0 and user_ids.0, which no form field looks up,
// so anything without a plain field key has to reach the user as a toast.
function hasFieldErrors(error: Error): boolean {
  return (
    error instanceof ApiError &&
    Object.keys(error.body.errors ?? {}).some((key) => !key.includes('.'))
  )
}

function reportError(
  handleMutationError: (error: Error) => void,
  fallback: string,
): (error: Error) => void {
  return (error) => {
    handleMutationError(error)

    if (!hasFieldErrors(error)) {
      toast.error(apiMessage(error, fallback))
    }
  }
}

export const Route = createFileRoute('/_app/wallets/$organisationId')({
  head: () => ({ meta: [{ title: 'Wallets - MyJunea' }] }),
  beforeLoad: async ({ params, context }) => {
    const organisationId = Number(params.organisationId)
    if (Number.isNaN(organisationId)) {
      throw redirect({ to: '/wallets' })
    }

    const organisation = await context.queryClient.ensureQueryData(
      organisationQueryOptions(organisationId),
    )

    if (!organisation.company_wallet_enabled) {
      throw redirect({ to: '/wallets' })
    }

    return { organisation }
  },
  component: CompanyWalletOrganisationPage,
})

function CompanyWalletOrganisationPage(): React.ReactElement {
  const queryClient = useQueryClient()
  const { organisationId: organisationIdParam } = Route.useParams()
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: 300 })
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [grantDialogOpen, setGrantDialogOpen] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  const grantErrors = useServerErrors()
  const addErrors = useServerErrors()

  const organisationId = Number(organisationIdParam)

  const { data: members, isLoading } = useQuery({
    ...walletUsersQueryOptions(organisationId, { search: debouncedSearch || undefined }),
    placeholderData: keepPreviousData,
  })

  function invalidateMembers(): void {
    void queryClient.invalidateQueries({ queryKey: queryKeys.walletUsers.all(organisationId) })
  }

  const grantMutation = useMutation({
    mutationFn: async (values: { mode: WalletTopupMode; amount: number }) =>
      grantWalletCredit(organisationId, { user_ids: selectedIds, ...values }),
    onSuccess: (response) => {
      grantErrors.clearServerErrors()
      invalidateMembers()
      toast.success(
        `${formatCurrencyFromDecimal(Number(response.data.total_amount))} granted to ${response.data.granted_count} ${response.data.granted_count === 1 ? 'member' : 'members'}`,
        response.data.skipped_count > 0
          ? {
              description: `${response.data.skipped_count} already had this amount or more, so they got nothing.`,
            }
          : undefined,
      )
      setGrantDialogOpen(false)
      setSelectedIds([])
    },
    onError: (error) => {
      // A bulk grant commits per member, so a failure halfway through leaves the
      // earlier members already credited. Refetch so the table cannot show stale
      // "last grant" values and invite a second grant on top of the first.
      invalidateMembers()
      reportError(grantErrors.handleMutationError, 'Failed to grant credit')(error)
    },
  })

  const settingMutation = useMutation({
    mutationFn: async (values: { mode: WalletTopupMode; amount: number }) =>
      updateWalletSetting(organisationId, { user_ids: selectedIds, ...values }),
    onSuccess: () => {
      grantErrors.clearServerErrors()
      invalidateMembers()
      toast.success('Monthly setting saved')
      setGrantDialogOpen(false)
      setSelectedIds([])
    },
    onError: reportError(grantErrors.handleMutationError, 'Failed to save the monthly setting'),
  })

  const clearSettingMutation = useMutation({
    mutationFn: async () => clearWalletSetting(organisationId, { user_ids: selectedIds }),
    onSuccess: () => {
      invalidateMembers()
      toast.success('Monthly setting removed')
      setSelectedIds([])
    },
    onError: (error) => {
      toast.error(apiMessage(error, 'Failed to remove the monthly setting'))
    },
  })

  const addMutation = useMutation({
    mutationFn: async (emails: string[]) => addWalletUsers(organisationId, { emails }),
    onSuccess: () => {
      addErrors.clearServerErrors()
      invalidateMembers()
      void queryClient.invalidateQueries({ queryKey: queryKeys.organisations.all })
    },
    onError: reportError(addErrors.handleMutationError, 'Failed to add members'),
  })

  const detachMutation = useMutation({
    mutationFn: async (member: WalletUser) => deleteWalletUser(organisationId, member.id),
    onSuccess: (_response, member) => {
      invalidateMembers()
      void queryClient.invalidateQueries({ queryKey: queryKeys.organisations.all })
      // Detaching removes an admin from System managers too.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organisationUsers.all(organisationId),
      })
      setSelectedIds((current) => current.filter((id) => id !== member.id))
      toast.success('Member removed')
    },
    onError: (error) => {
      toast.error(apiMessage(error, 'Failed to remove the member'))
    },
  })

  function handleGrantSubmit(values: {
    schedule: GrantSchedule
    mode: WalletTopupMode
    amount: number
  }): void {
    const { schedule, ...grant } = values
    if (schedule === 'once') {
      grantMutation.mutate(grant)
    } else {
      settingMutation.mutate(grant)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Wallets"
        description={<CompanyWalletIntro />}
        defaultFiltersOpen={true}
        action={
          <Button
            onClick={() => {
              addMutation.reset()
              setAddDialogOpen(true)
            }}
          >
            Add members
          </Button>
        }
        filters={
          <>
            <OrganisationNavSelect
              currentOrganisationId={organisationId}
              baseRoute="/wallets/$organisationId"
            />
            <SearchInput
              placeholder="Search members..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              className="w-48"
            />
          </>
        }
      />

      <WalletMembersTable
        members={members ?? []}
        isLoading={isLoading}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onDetach={(member) => detachMutation.mutate(member)}
        isDetaching={detachMutation.isPending}
      />

      <WalletSelectionBar
        selectedCount={selectedIds.length}
        onGrantCredit={() => setGrantDialogOpen(true)}
        onRemoveMonthlySetting={() => clearSettingMutation.mutate()}
        onClearSelection={() => setSelectedIds([])}
        isRemovingSetting={clearSettingMutation.isPending}
      />

      <GrantCreditDialog
        open={grantDialogOpen}
        onOpenChange={setGrantDialogOpen}
        selectedCount={selectedIds.length}
        isPending={grantMutation.isPending || settingMutation.isPending}
        serverErrors={grantErrors.serverErrors}
        onSubmit={handleGrantSubmit}
      />

      <AddWalletUsersDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        results={addMutation.data?.data.results}
        isPending={addMutation.isPending}
        serverErrors={addErrors.serverErrors}
        onSubmit={(emails) => addMutation.mutate(emails)}
        onAddMore={() => addMutation.reset()}
      />
    </div>
  )
}
